import type { LucideIcon } from "lucide-react";
import {
	Edit,
	Download,
	Filter,
	Loader2,
	MoreVertical,
	Plus,
	RotateCcw,
	Save,
	Search,
	Settings2,
	X,
} from "lucide-react";
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { AdvancedFilterPanel } from "@/components/AdvancedFilterPanel";
import { ConfigureColumnsDialog } from "@/components/ConfigureColumnsDialog";
import { TableColumnHeader } from "@/components/TableColumnHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "@/components/ui/pagination";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ColumnConfig<T = any> {
	key: string;
	label: string;
	sortable?: boolean;
	filterable?: boolean;
	filterConfig?: {
		type:
			| "text"
			| "number"
			| "date-range"
			| "select"
			| "multi-select"
			| "boolean";
		options?: string[];
	};
	align?: "left" | "center" | "right";
	render: (
		row: T,
		isEditMode: boolean,
		onUpdate: (field: keyof T, value: any) => void,
	) => React.ReactNode;
	width?: number;
	minWidth?: number;
}

export interface PrimaryAction {
	label: string;
	icon?: LucideIcon;
	onClick: () => void;
}

export interface EditModeConfig<T = any> {
	enabled: boolean;
	onSave?: (data: T[]) => void | Promise<void>;
	onCancel?: () => void;
}

export interface TotalsConfig {
	enabled: boolean;
	calculate?: (data: any[], columnKey: string) => string | number | null;
}

export interface MenuAction {
	label: string;
	icon?: LucideIcon;
	onClick: () => void;
	separator?: boolean;
}

// ============================================================================
// PROPS INTERFACE
// ============================================================================

export interface MaximusTableProps<T = any> {
	data: T[];
	columns: ColumnConfig<T>[];
	title?: string;
	subtitle?: string;
	searchPlaceholder?: string;
	primaryAction?: PrimaryAction;
	editMode?: EditModeConfig<T>;
	totals?: TotalsConfig;
	menuActions?: MenuAction[];
	showAdvancedFilter?: boolean;
	showMenu?: boolean;
	showSearch?: boolean;
	enableRowSelection?: boolean;
	enableZebraStriping?: boolean;
	onSearch?: (data: T[], query: string) => T[];
	onDataChange?: (data: T[]) => void;
	onRowSelectionChange?: (selectedIds: string[]) => void;
	initialVisibleColumns?: string[];
	initialColumnWidths?: Record<string, number>;
	rowIdAccessor?: keyof T | ((row: T) => string);
	/** Optional content rendered below the title/search row, above the table */
	headerContent?: React.ReactNode;
	/** Optional content rendered to the left of the three-dot menu in the header row */
	headerActions?: React.ReactNode;
	/** Infinite scroll configuration */
	infiniteScroll?: {
		enabled: boolean;
		initialRows?: number;
		loadMoreRows?: number;
	};
	/** Pagination configuration — mutually exclusive with infiniteScroll */
	pagination?: {
		enabled: boolean;
		defaultPageSize?: number;
		pageSizeOptions?: number[];
		/** Server-side total item count — when set, the table uses this for pagination
		 * calculations instead of the client-side data length and skips internal slicing. */
		totalItems?: number;
		/** Called when the user navigates to a different page (server-side pagination). */
		onPageChange?: (page: number) => void;
		/** Called when the user changes the page size (server-side pagination). */
		onPageSizeChange?: (pageSize: number) => void;
		/** Controlled page number — syncs the table's internal page state with the parent. */
		controlledPage?: number;
	};
	/** External sort handler — when provided, client-side sorting is disabled and
	 * the callback fires with (columnKey, direction) so the parent can sort via API.
	 * Pass null direction when sort is cleared. */
	onSortChange?: (
		column: string | null,
		direction: "asc" | "desc" | null,
	) => void;
	/** Show a loading spinner in the table body */
	isLoading?: boolean;
	/** External filter handler — when provided, the callback fires with the current column filters
	 * so the parent can filter via API. When set, client-side filtering via applyFilters is skipped. */
	onFilterChange?: (filters: ColumnFilters) => void;
	/** Callback invoked when the user saves column configuration via the Configure Columns dialog.
	 * Receives the new visible column keys so the parent can persist them. */
	onColumnConfigSave?: (visibleKeys: string[]) => void;
	/** Optional override for the Export to Excel menu item. When provided, this
	 * callback is invoked instead of the built-in client-side CSV export. */
	onExportToExcel?: () => void;
	/** External search handler — when provided, fires with the current search query
	 * so the parent can search via API. When set, client-side search via onSearch is skipped. */
	onSearchChange?: (query: string) => void;
	/** Initial column filters to pre-populate the advanced filter state on mount. */
	initialColumnFilters?: ColumnFilters;
}

// ============================================================================
// INTERNAL TYPES
// ============================================================================

type SortColumn = string | null;
type SortDirection = "asc" | "desc" | null;

export interface ColumnFilters {
	[key: string]: {
		value?: string | number | boolean | string[] | null;
		mode?: "contains" | "equals" | "isEmpty";
		dateFrom?: Date;
		dateTo?: Date;
		numberFrom?: number;
		numberTo?: number;
	};
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function MaximusTableReusable<T = any>({
	data,
	columns,
	title,
	subtitle,
	searchPlaceholder = "Search...",
	primaryAction,
	editMode,
	totals,
	menuActions = [],
	showAdvancedFilter = true,
	showMenu = true,
	showSearch = true,
	enableRowSelection = true,
	enableZebraStriping = true,
	onSearch,
	onDataChange,
	onRowSelectionChange,
	initialVisibleColumns,
	initialColumnWidths,
	rowIdAccessor = "id" as keyof T,
	headerContent,
	headerActions,
	infiniteScroll,
	pagination,
	onSortChange,
	isLoading,
	onFilterChange,
	onColumnConfigSave,
	onExportToExcel,
	onSearchChange,
	initialColumnFilters,
}: MaximusTableProps<T>) {
	// ========== STATE ==========

	const [tableData, setTableData] = useState<T[]>(data);
	const [originalData, setOriginalData] = useState<T[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [isEditMode, setIsEditMode] = useState(false);
	const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
	const [isConfigureColumnsOpen, setIsConfigureColumnsOpen] = useState(false);
	const [sortColumn, setSortColumn] = useState<SortColumn>(null);
	const [sortDirection, setSortDirection] = useState<SortDirection>(null);
	const [columnFilters, setColumnFilters] = useState<ColumnFilters>(initialColumnFilters ?? {});
	const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
	const [pinnedLeftColumns, setPinnedLeftColumns] = useState<string[]>([]);
	const [pinnedRightColumns, setPinnedRightColumns] = useState<string[]>([]);
	const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(
		initialVisibleColumns || columns.map((col) => col.key),
	);

	const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
		() => {
			const defaultWidths: Record<string, number> = { checkbox: 48 };
			columns.forEach((col) => {
				defaultWidths[col.key] = col.width || 120;
			});
			return { ...defaultWidths, ...initialColumnWidths };
		},
	);

	const [resizingColumn, setResizingColumn] = useState<string | null>(null);
	const [resizeStartX, setResizeStartX] = useState<number>(0);
	const [resizeStartWidth, setResizeStartWidth] = useState<number>(0);

	// Infinite scroll state
	const [visibleRows, setVisibleRows] = useState(
		infiniteScroll?.initialRows ?? 50,
	);
	const sentinelRef = useRef<HTMLDivElement>(null);

	// Debounce timer for onSearchChange
	const searchDebounceRef = useRef<number | null>(null);
	useEffect(() => {
		return () => {
			if (searchDebounceRef.current) {
				clearTimeout(searchDebounceRef.current);
			}
		};
	}, []);

	// Pagination state
	const defaultPageSize = pagination?.defaultPageSize ?? 25;
	const pageSizeOptions = pagination?.pageSizeOptions ?? [25, 50, 100, 200];
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(defaultPageSize);

	// Sync controlled page from parent (server-side pagination)
	useEffect(() => {
		if (pagination?.controlledPage !== undefined) {
			setCurrentPage(pagination.controlledPage);
		}
	}, [pagination?.controlledPage]);

	const tableCardRef = useRef<HTMLDivElement>(null);
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const [tableMaxHeight, setTableMaxHeight] = useState<string>(
		"calc(100vh - 200px)",
	);

	// ========== EFFECTS ==========

	useEffect(() => {
		setTableData(data);
	}, [data]);

	useEffect(() => {
		const calculateHeight = () => {
			if (tableCardRef.current) {
				const rect = tableCardRef.current.getBoundingClientRect();
				const availableHeight = window.innerHeight - rect.top - 8;
				const minTableHeight = 350;
				setTableMaxHeight(`${Math.max(availableHeight, minTableHeight)}px`);
			}
		};
		calculateHeight();
		window.addEventListener("resize", calculateHeight);
		return () => window.removeEventListener("resize", calculateHeight);
	}, []);

	useEffect(() => {
		if (resizingColumn) {
			const handleResizeMove = (e: MouseEvent) => {
				const diff = e.clientX - resizeStartX;
				const newWidth = Math.max(30, resizeStartWidth + diff);
				setColumnWidths((prev) => ({ ...prev, [resizingColumn]: newWidth }));
			};
			const handleResizeEnd = () => {
				setResizingColumn(null);
			};
			document.addEventListener("mousemove", handleResizeMove);
			document.addEventListener("mouseup", handleResizeEnd);
			return () => {
				document.removeEventListener("mousemove", handleResizeMove);
				document.removeEventListener("mouseup", handleResizeEnd);
			};
		}
	}, [resizingColumn, resizeStartX, resizeStartWidth]);

	useEffect(() => {
		if (onDataChange && tableData !== data) {
			onDataChange(tableData);
		}
	}, [tableData, onDataChange, data]);

	useEffect(() => {
		if (onRowSelectionChange) {
			onRowSelectionChange(Array.from(selectedRows));
		}
	}, [selectedRows, onRowSelectionChange]);

	// ========== HELPER FUNCTIONS ==========

	const getRowId = useCallback(
		(row: T): string => {
			if (typeof rowIdAccessor === "function") {
				return rowIdAccessor(row);
			}
			return String(row[rowIdAccessor]);
		},
		[rowIdAccessor],
	);

	const defaultSearch = useCallback((data: T[], query: string): T[] => {
		if (!query.trim()) return data;
		const lowerQuery = query.toLowerCase();
		return data.filter((row) => {
			return Object.values(row as any).some((value) => {
				if (value === null || value === undefined) return false;
				return String(value).toLowerCase().includes(lowerQuery);
			});
		});
	}, []);

	const applyFilters = useCallback(
		(data: T[]) => {
			return data.filter((row) => {
				for (const [column, filter] of Object.entries(columnFilters)) {
					const value = (row as any)[column];

					if (filter.mode === "isEmpty") {
						if (value !== null && value !== undefined && value !== "") return false;
						continue;
					}

					if (
						filter.value !== undefined &&
						filter.value !== null &&
						filter.value !== ""
					) {
						if (typeof value === "string") {
							const filterValue = String(filter.value).toLowerCase();
							const rowValue = value.toLowerCase();
							if (filter.mode === "equals") {
								if (rowValue !== filterValue) return false;
							} else {
								if (!rowValue.includes(filterValue)) return false;
							}
						} else if (typeof value === "boolean") {
							if (value !== filter.value) return false;
						} else {
							if (
								String(value).toLowerCase() !==
								String(filter.value).toLowerCase()
							)
								return false;
						}
					}

					if (filter.dateFrom || filter.dateTo) {
						if (value instanceof Date) {
							if (filter.dateFrom && value < filter.dateFrom) return false;
							if (filter.dateTo) {
								const endOfDay = new Date(filter.dateTo);
								endOfDay.setHours(23, 59, 59, 999);
								if (value > endOfDay) return false;
							}
						}
					}

					if (
						filter.numberFrom !== undefined ||
						filter.numberTo !== undefined
					) {
						if (typeof value === "number") {
							if (filter.numberFrom !== undefined && value < filter.numberFrom)
								return false;
							if (filter.numberTo !== undefined && value > filter.numberTo)
								return false;
						}
					}
				}
				return true;
			});
		},
		[columnFilters],
	);

	const applySort = useCallback(
		(data: T[]) => {
			if (!sortColumn || !sortDirection) return data;
			return [...data].sort((a, b) => {
				const aValue = (a as any)[sortColumn];
				const bValue = (b as any)[sortColumn];

				if (aValue === null || aValue === undefined)
					return sortDirection === "asc" ? 1 : -1;
				if (bValue === null || bValue === undefined)
					return sortDirection === "asc" ? -1 : 1;

				if (aValue instanceof Date && bValue instanceof Date) {
					return sortDirection === "asc"
						? aValue.getTime() - bValue.getTime()
						: bValue.getTime() - aValue.getTime();
				}

				if (typeof aValue === "number" && typeof bValue === "number") {
					return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
				}

				const aStr = String(aValue).toLowerCase();
				const bStr = String(bValue).toLowerCase();
				if (sortDirection === "asc") {
					return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
				} else {
					return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
				}
			});
		},
		[sortColumn, sortDirection],
	);

	// ========== FILTERED DATA ==========

	const filteredData = useMemo(() => {
		let result = tableData;
		if (searchQuery.trim() && !onSearchChange) {
			result = onSearch
				? onSearch(result, searchQuery)
				: defaultSearch(result, searchQuery);
		}
		if (!onFilterChange) {
			result = applyFilters(result);
		}
		if (!onSortChange) {
			result = applySort(result);
		}
		return result;
	}, [
		tableData,
		searchQuery,
		onSearch,
		onSearchChange,
		defaultSearch,
		applyFilters,
		applySort,
		onSortChange,
		onFilterChange,
	]);

	// Reset visible rows when filtered data changes (filter/search/sort)
	useEffect(() => {
		setVisibleRows(infiniteScroll?.initialRows ?? 50);
		if (pagination?.totalItems === undefined) {
			setCurrentPage(1);
		}
	}, [filteredData, infiniteScroll?.initialRows, pagination?.totalItems]);

	// ========== INFINITE SCROLL ==========

	const displayedData = useMemo(() => {
		if (pagination?.enabled) {
			if (pagination.totalItems !== undefined) {
				return filteredData;
			}
			const start = (currentPage - 1) * pageSize;
			return filteredData.slice(start, start + pageSize);
		}
		if (!infiniteScroll?.enabled) return filteredData;
		return filteredData.slice(0, visibleRows);
	}, [
		filteredData,
		visibleRows,
		infiniteScroll?.enabled,
		pagination?.enabled,
		pagination?.totalItems,
		currentPage,
		pageSize,
	]);

	// Pagination derived values
	const paginationTotal = pagination?.totalItems ?? filteredData.length;
	const totalPages = pagination?.enabled
		? Math.max(1, Math.ceil(paginationTotal / pageSize))
		: 1;

	useEffect(() => {
		if (!infiniteScroll?.enabled || !sentinelRef.current) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && visibleRows < filteredData.length) {
					setVisibleRows((prev) => prev + (infiniteScroll?.loadMoreRows ?? 50));
				}
			},
			{ threshold: 0.1 },
		);
		observer.observe(sentinelRef.current);
		return () => observer.disconnect();
	}, [
		infiniteScroll?.enabled,
		visibleRows,
		filteredData.length,
		infiniteScroll?.loadMoreRows,
	]);

	// ========== EVENT HANDLERS ==========

	const handleSort = (column: string) => {
		let newColumn: string | null = column;
		let newDirection: "asc" | "desc" | null = "asc";

		if (sortColumn === column) {
			if (sortDirection === "asc") {
				newDirection = "desc";
			} else if (sortDirection === "desc") {
				newColumn = null;
				newDirection = null;
			}
		}

		setSortColumn(newColumn);
		setSortDirection(newDirection);

		if (onSortChange) {
			onSortChange(newColumn, newDirection);
		}
	};

	const handleColumnFilterChange = (column: string, filter: any) => {
		setColumnFilters((prev) => {
			const newFilters =
				filter === null
					? (() => {
							const f = { ...prev };
							delete f[column];
							return f;
						})()
					: { ...prev, [column]: filter };
			if (onFilterChange) {
				onFilterChange(newFilters);
			}
			return newFilters;
		});
	};

	const handleEdit = () => {
		setOriginalData([...tableData]);
		setIsEditMode(true);
	};

	const handleSave = async () => {
		if (editMode?.onSave) {
			await editMode.onSave(tableData);
		}
		setIsEditMode(false);
		setOriginalData([]);
	};

	const handleCancel = () => {
		setTableData(originalData);
		setIsEditMode(false);
		setOriginalData([]);
		if (editMode?.onCancel) {
			editMode.onCancel();
		}
	};

	const handleUpdateRow = useCallback(
		(rowId: string, field: keyof T, value: any) => {
			setTableData((prevData) =>
				prevData.map((row) =>
					getRowId(row) === rowId ? { ...row, [field]: value } : row,
				),
			);
		},
		[getRowId],
	);

	const handlePinColumn = (column: string, side: "left" | "right") => {
		if (side === "left") {
			setPinnedLeftColumns((prev) => {
				if (prev.includes(column)) {
					return prev.filter((col) => col !== column);
				}
				setPinnedRightColumns((prevRight) =>
					prevRight.filter((col) => col !== column),
				);
				return [...prev, column];
			});
		} else {
			setPinnedRightColumns((prev) => {
				if (prev.includes(column)) {
					return prev.filter((col) => col !== column);
				}
				setPinnedLeftColumns((prevLeft) =>
					prevLeft.filter((col) => col !== column),
				);
				return [...prev, column];
			});
		}
	};

	const handleExportToExcel = () => {
		const exportColumns = columns.filter((col) =>
			visibleColumnKeys.includes(col.key),
		);
		const headers = exportColumns.map((col) => col.label).join(",");
		const rows = filteredData
			.map((row) =>
				exportColumns
					.map((col) => {
						const value = (row as any)[col.key];
						if (value === null || value === undefined) return "";
						if (typeof value === "string" && value.includes(","))
							return `"${value}"`;
						if (value instanceof Date) return value.toLocaleDateString();
						if (Array.isArray(value)) return `"${value.join(", ")}"`;
						return value;
					})
					.join(","),
			)
			.join("\n");

		const csv = `${headers}\n${rows}`;
		const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute(
			"download",
			`${(title ?? "table").toLowerCase().replace(/\s+/g, "-")}-export.csv`,
		);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const handleResetColumnWidths = () => {
		const defaultWidths: Record<string, number> = { checkbox: 48 };
		columns.forEach((col) => {
			defaultWidths[col.key] = col.width || 120;
		});
		setColumnWidths(defaultWidths);
	};

	const handleResizeStart = (e: React.MouseEvent, columnKey: string) => {
		e.preventDefault();
		e.stopPropagation();
		setResizingColumn(columnKey);
		setResizeStartX(e.clientX);
		setResizeStartWidth(columnWidths[columnKey] || 120);
	};

	const handleResizeDoubleClick = (columnKey: string) => {
		const cells = document.querySelectorAll(`[data-column-key="${columnKey}"]`);
		let maxContentWidth = 0;

		cells.forEach((cell) => {
			const htmlElement = cell as HTMLElement;
			const computedStyle = window.getComputedStyle(htmlElement);
			const textContent = htmlElement.textContent || "";

			const measureSpan = document.createElement("span");
			measureSpan.style.position = "absolute";
			measureSpan.style.visibility = "hidden";
			measureSpan.style.whiteSpace = "nowrap";
			measureSpan.style.display = "inline-block";
			measureSpan.style.fontFamily = computedStyle.fontFamily;
			measureSpan.style.fontSize = computedStyle.fontSize;
			measureSpan.style.fontWeight = computedStyle.fontWeight;
			measureSpan.style.lineHeight = computedStyle.lineHeight;
			measureSpan.style.letterSpacing = computedStyle.letterSpacing;
			measureSpan.textContent = textContent;

			document.body.appendChild(measureSpan);
			const contentWidth = measureSpan.getBoundingClientRect().width;
			document.body.removeChild(measureSpan);

			const totalWidth = contentWidth + 32;
			maxContentWidth = Math.max(maxContentWidth, totalWidth);
		});

		const minWidth = columnKey === "checkbox" ? 60 : 120;
		const extraSpace = columnKey === "checkbox" ? 10 : 8;
		const finalWidth = Math.max(minWidth, maxContentWidth + extraSpace);

		setColumnWidths((prev) => ({ ...prev, [columnKey]: finalWidth }));
	};

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			setSelectedRows(new Set(filteredData.map((row) => getRowId(row))));
		} else {
			setSelectedRows(new Set());
		}
	};

	const handleSelectRow = (rowId: string, checked: boolean) => {
		const newSelected = new Set(selectedRows);
		if (checked) {
			newSelected.add(rowId);
		} else {
			newSelected.delete(rowId);
		}
		setSelectedRows(newSelected);
	};

	// ========== COLUMN CONFIGURATION ==========

	const orderedColumns = useMemo(() => {
		const visibleConfigs = columns.filter((col) =>
			visibleColumnKeys.includes(col.key),
		);
		const configMap = new Map(visibleConfigs.map((col) => [col.key, col]));

		const orderedByVisibility = visibleColumnKeys
			.map((key) => configMap.get(key))
			.filter((col): col is ColumnConfig<T> => col !== undefined);

		const pinnedLeftConfigs = orderedByVisibility.filter((col) =>
			pinnedLeftColumns.includes(col.key),
		);
		const pinnedRightConfigs = orderedByVisibility.filter((col) =>
			pinnedRightColumns.includes(col.key),
		);
		const unpinnedConfigs = orderedByVisibility.filter(
			(col) =>
				!pinnedLeftColumns.includes(col.key) &&
				!pinnedRightColumns.includes(col.key),
		);

		pinnedLeftConfigs.sort(
			(a, b) =>
				pinnedLeftColumns.indexOf(a.key) - pinnedLeftColumns.indexOf(b.key),
		);
		pinnedRightConfigs.sort(
			(a, b) =>
				pinnedRightColumns.indexOf(a.key) - pinnedRightColumns.indexOf(b.key),
		);

		return [...pinnedLeftConfigs, ...unpinnedConfigs, ...pinnedRightConfigs];
	}, [columns, pinnedLeftColumns, pinnedRightColumns, visibleColumnKeys]);

	// ========== STYLING FUNCTIONS ==========

	const getStickyStyle = (columnKey: string): React.CSSProperties => {
		if (columnKey === "checkbox") {
			return {
				position: "sticky" as const,
				left: "0px",
				zIndex: 50,
				boxShadow: "inset -2px 0 0 0 var(--border)",
				width: `${columnWidths.checkbox}px`,
				maxWidth: `${columnWidths.checkbox}px`,
			};
		}

		if (pinnedLeftColumns.includes(columnKey)) {
			const pinnedIndex = pinnedLeftColumns.indexOf(columnKey);
			let leftOffset = enableRowSelection ? columnWidths.checkbox : 0;
			for (let i = 0; i < pinnedIndex; i++) {
				leftOffset += columnWidths[pinnedLeftColumns[i]] || 120;
			}
			return {
				position: "sticky" as const,
				left: `${leftOffset}px`,
				zIndex: 50,
				boxShadow: "inset -2px 0 0 0 var(--border)",
				width: `${columnWidths[columnKey] || 120}px`,
				maxWidth: `${columnWidths[columnKey] || 120}px`,
			};
		}

		if (pinnedRightColumns.includes(columnKey)) {
			const pinnedIndex = pinnedRightColumns.indexOf(columnKey);
			let rightOffset = 0;
			for (let i = pinnedIndex + 1; i < pinnedRightColumns.length; i++) {
				rightOffset += columnWidths[pinnedRightColumns[i]] || 120;
			}
			return {
				position: "sticky" as const,
				right: `${rightOffset}px`,
				zIndex: 50,
				boxShadow: "inset 2px 0 0 0 hsl(var(--border))",
				width: `${columnWidths[columnKey] || 120}px`,
				maxWidth: `${columnWidths[columnKey] || 120}px`,
			};
		}

		return {
			width: `${columnWidths[columnKey] || 120}px`,
			maxWidth: `${columnWidths[columnKey] || 120}px`,
		};
	};

	const getHeaderStyle = (columnKey: string): React.CSSProperties => {
		const baseStyle = getStickyStyle(columnKey);
		const isPinned = baseStyle.position === "sticky";
		return {
			...baseStyle,
			backgroundColor: "var(--white)",
			zIndex: isPinned ? 50 : 41,
		};
	};

	const getBodyCellStyle = (
		columnKey: string,
		rowIndex: number,
	): React.CSSProperties => {
		const baseStyle = getStickyStyle(columnKey);
		const isPinned = baseStyle.position === "sticky";
		const isEvenRow = rowIndex % 2 === 0;
		if (isPinned) {
			return {
				...baseStyle,
				backgroundColor: enableZebraStriping
					? isEvenRow
						? "var(--neutral-50)"
						: "var(--white)"
					: "var(--white)",
				zIndex: 10,
			};
		}
		return {
			...baseStyle,
			backgroundColor: "inherit",
		};
	};

	const getFooterStyle = (
		columnKey: string,
		isFirst: boolean = false,
		isLast: boolean = false,
	): React.CSSProperties => {
		const baseStyle = getStickyStyle(columnKey);
		const isPinned = baseStyle.position === "sticky";

		let boxShadow = "0 -1px 0 0 hsl(var(--border))";
		if (isPinned && baseStyle.boxShadow) {
			boxShadow = `${boxShadow}, ${baseStyle.boxShadow}`;
		}

		let borderRadius = "";
		if (isFirst) borderRadius = "0 0 0 0.5rem";
		if (isLast) borderRadius = "0 0 0.5rem 0";

		return {
			...baseStyle,
			backgroundColor: "var(--white)",
			boxShadow,
			zIndex: isPinned ? 50 : 41,
			padding: "8px 6px 10px 6px",
			borderRadius,
		};
	};

	const getAlignmentClass = (align?: "left" | "center" | "right"): string => {
		switch (align) {
			case "left":
				return "text-left";
			case "center":
				return "text-center";
			case "right":
				return "text-right";
			default:
				return "text-left";
		}
	};

	// ========== TOTALS CALCULATION ==========

	const calculateTotals = () => {
		if (!totals?.enabled || !totals.calculate) return {};
		const totalsRow: Record<string, string | number | null> = {};
		columns.forEach((col) => {
			const total = totals.calculate!(filteredData, col.key);
			if (total !== null) {
				totalsRow[col.key] = total;
			}
		});
		return totalsRow;
	};

	const totalsRow = totals?.enabled ? calculateTotals() : {};

	// ========== RENDER ==========

	return (
		<>
			<Card
				ref={tableCardRef}
				className="mt-3 flex flex-col overflow-hidden"
				style={{ maxHeight: tableMaxHeight }}
			>
				<div className="pt-3 px-2 pb-0 flex-shrink-0">
					<div className="flex items-center justify-between mb-2 px-2">
						{/* Left side */}
						<div className="flex items-center gap-3">
							{title && <h3 className="text-lg font-semibold">{title}</h3>}
							{subtitle && (
								<span className="text-sm text-neutral-500">{subtitle}</span>
							)}
							{!subtitle && !pagination?.enabled && (
								<span className="text-sm text-neutral-500">
									{`Showing ${filteredData.length} of ${tableData.length} results`}
								</span>
							)}
							{showSearch && (
								<div className="relative">
									<Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
									<Input
										type="text"
										placeholder={searchPlaceholder}
										value={searchQuery}
										onChange={(e) => {
										const val = e.target.value;
										setSearchQuery(val);
										if (onSearchChange) {
											if (searchDebounceRef.current) {
												clearTimeout(searchDebounceRef.current);
											}
											searchDebounceRef.current = window.setTimeout(() => {
												onSearchChange(val);
											}, 300);
										}
									}}
										className="pl-9 w-64"
									/>
								</div>
							)}
							{showAdvancedFilter && (
								<Button
									variant="outline"
									size="sm"
									onClick={() => setIsAdvancedFilterOpen(!isAdvancedFilterOpen)}
									className="gap-1.5"
									data-advanced-filter-toggle
								>
									<Filter className="h-4 w-4" />
									Filter
									{Object.keys(columnFilters).filter((k) => {
										const f = columnFilters[k];
										if (!f) return false;
										if (f.mode === "isEmpty") return true;
										if (
											f.value !== undefined &&
											f.value !== null &&
											f.value !== ""
										)
											return true;
										if (f.dateFrom || f.dateTo) return true;
										if (f.numberFrom !== undefined || f.numberTo !== undefined)
											return true;
										return false;
									}).length > 0 && (
										<Badge
											variant="secondary"
											className="ml-0.5 px-1.5 py-0 text-xs min-w-[1.25rem] justify-center"
										>
											{
												Object.keys(columnFilters).filter((k) => {
													const f = columnFilters[k];
													if (!f) return false;
													if (f.mode === "isEmpty") return true;
													if (
														f.value !== undefined &&
														f.value !== null &&
														f.value !== ""
													)
														return true;
													if (f.dateFrom || f.dateTo) return true;
													if (
														f.numberFrom !== undefined ||
														f.numberTo !== undefined
													)
														return true;
													return false;
												}).length
											}
										</Badge>
									)}
								</Button>
							)}
						</div>

						{/* Right side */}
						<div className="flex items-center gap-2">
							{editMode?.enabled && (
								<>
									{!isEditMode ? (
										<Button variant="secondary" onClick={handleEdit}>
											<Edit className="h-4 w-4 mr-2" />
											Edit
										</Button>
									) : (
										<>
											<Button variant="ghost" onClick={handleCancel}>
												<X className="h-4 w-4 mr-2" />
												Cancel
											</Button>
											<Button variant="secondary" onClick={handleSave}>
												<Save className="h-4 w-4 mr-2" />
												Save
											</Button>
										</>
									)}
								</>
							)}
							{primaryAction && (
								<Button onClick={primaryAction.onClick}>
									{primaryAction.icon && (
										<primaryAction.icon className="h-4 w-4 mr-2" />
									)}
									{primaryAction.label}
								</Button>
							)}
							{headerActions && (
								<div className="flex items-center gap-2">{headerActions}</div>
							)}
							{showMenu && (
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button variant="ghost" size="icon">
											<MoreVertical className="h-4 w-4" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" className="w-56">
										<DropdownMenuItem onClick={onExportToExcel ?? handleExportToExcel}>
											<Download className="mr-2 h-4 w-4" />
											<span>Export to Excel</span>
										</DropdownMenuItem>
										<DropdownMenuItem onClick={handleResetColumnWidths}>
											<RotateCcw className="mr-2 h-4 w-4" />
											<span>Reset Column Widths</span>
										</DropdownMenuItem>
										<DropdownMenuSeparator />
										<DropdownMenuItem
											onClick={() => setIsConfigureColumnsOpen(true)}
										>
											<Settings2 className="mr-2 h-4 w-4" />
											<span>Configure Columns</span>
										</DropdownMenuItem>
										{menuActions.map((action, index) => (
											<React.Fragment key={index}>
												{action.separator && <DropdownMenuSeparator />}
												<DropdownMenuItem onClick={action.onClick}>
													{action.icon && (
														<action.icon className="mr-2 h-4 w-4" />
													)}
													<span>{action.label}</span>
												</DropdownMenuItem>
											</React.Fragment>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							)}
						</div>
					</div>
				</div>

				{headerContent && (
					<div className="px-3 pb-2 flex-shrink-0 border-b">
						{headerContent}
					</div>
				)}

				<div ref={tableContainerRef} className="overflow-auto rounded-b-lg">
					<table className="w-full caption-bottom text-xs table-fixed bg-white border-separate border-spacing-0 relative">
						<thead className="sticky top-0 z-40 bg-white after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-border after:z-50">
							<TableRow>
								{enableRowSelection && (
									<TableHead
										className="w-9 pr-2 font-semibold border-r-0 relative text-center"
										style={getHeaderStyle("checkbox")}
										data-column-key="checkbox"
									>
										<div className="flex items-center justify-center">
											<Checkbox
												checked={
													displayedData.length > 0 &&
													displayedData.every((row) =>
														selectedRows.has(getRowId(row)),
													)
												}
												onCheckedChange={handleSelectAll}
												aria-label="Select all rows"
											/>
										</div>
										<div
											className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-info-500 hover:w-1.5 transition-all"
											onMouseDown={(e) => handleResizeStart(e, "checkbox")}
											onDoubleClick={() => handleResizeDoubleClick("checkbox")}
										/>
									</TableHead>
								)}
								{orderedColumns.map((col) => (
									<TableHead
										key={col.key}
										className={cn(
											"font-semibold relative",
											getAlignmentClass(col.align),
											"overflow-hidden text-ellipsis whitespace-nowrap",
											(pinnedLeftColumns.includes(col.key) ||
												pinnedRightColumns.includes(col.key)) &&
												"border-r-0",
										)}
										style={getHeaderStyle(col.key)}
										data-column-key={col.key}
									>
										<TableColumnHeader
											title={col.label}
											columnKey={col.key}
											sortable={col.sortable}
											filterable={col.filterable}
											filterConfig={col.filterConfig}
											sortDirection={
												sortColumn === col.key ? sortDirection : null
											}
											onSort={() => handleSort(col.key)}
											filter={columnFilters[col.key]}
											onFilterChange={(filter) =>
												handleColumnFilterChange(col.key, filter)
											}
											isPinnedLeft={pinnedLeftColumns.includes(col.key)}
											isPinnedRight={pinnedRightColumns.includes(col.key)}
											onPinLeft={() => handlePinColumn(col.key, "left")}
											onPinRight={() => handlePinColumn(col.key, "right")}
										/>
										<div
											className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-info-500 hover:w-1.5 transition-all z-50"
											onMouseDown={(e) => handleResizeStart(e, col.key)}
											onDoubleClick={() => handleResizeDoubleClick(col.key)}
										/>
									</TableHead>
								))}
							</TableRow>
						</thead>
						<tbody className="[&_tr]:border-b-0 [&_tr:hover]:bg-muted/50">
							{isLoading ? (
								<TableRow>
									<TableCell
										colSpan={
											(enableRowSelection ? 1 : 0) + orderedColumns.length
										}
										className="text-center py-12"
									>
										<div className="flex flex-col items-center justify-center gap-2">
											<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
											<span className="text-sm text-muted-foreground">
												Loading...
											</span>
										</div>
									</TableCell>
								</TableRow>
							) : displayedData.length > 0 ? (
								displayedData.map((row, index) => {
									const rowId = getRowId(row);
									return (
										<TableRow
											key={rowId}
											className={cn(
												"transition-none",
												enableZebraStriping && index % 2 === 0
													? "bg-neutral-50"
													: "bg-white",
											)}
										>
											{enableRowSelection && (
												<TableCell
													className="w-9 pr-2 border-r-0 text-center"
													style={getBodyCellStyle("checkbox", index)}
													data-column-key="checkbox"
												>
													<div className="flex items-center justify-center">
														<Checkbox
															checked={selectedRows.has(rowId)}
															onCheckedChange={(checked) =>
																handleSelectRow(rowId, checked as boolean)
															}
															aria-label={`Select row ${rowId}`}
														/>
													</div>
												</TableCell>
											)}
											{orderedColumns.map((col) => (
												<TableCell
													key={col.key}
													className={cn(
														getAlignmentClass(col.align),
														"overflow-hidden text-ellipsis whitespace-nowrap",
														(pinnedLeftColumns.includes(col.key) ||
															pinnedRightColumns.includes(col.key)) &&
															"border-r-0",
													)}
													style={getBodyCellStyle(col.key, index)}
													data-column-key={col.key}
												>
													{col.render(row, isEditMode, (field, value) =>
														handleUpdateRow(rowId, field, value),
													)}
												</TableCell>
											))}
										</TableRow>
									);
								})
							) : (
								<TableRow>
									<TableCell
										colSpan={
											(enableRowSelection ? 1 : 0) + orderedColumns.length
										}
										className="text-center text-neutral-500 py-8"
									>
										No data found.
									</TableCell>
								</TableRow>
							)}
						</tbody>
						{totals?.enabled && (
							<tfoot className="sticky bottom-0 z-40 bg-background rounded-b-lg after:content-[''] after:absolute after:top-0 after:left-0 after:right-0 after:h-[2px] after:bg-border after:z-50">
								<TableRow className="border-b-0">
									{enableRowSelection && (
										<TableCell
											className="w-12 pr-6 border-r-0 text-center font-semibold border-t-2"
											style={getFooterStyle("checkbox", true, false)}
											data-column-key="checkbox"
										/>
									)}
									{orderedColumns.map((col, index) => (
										<TableCell
											key={col.key}
											className={cn(
												getAlignmentClass(col.align),
												(pinnedLeftColumns.includes(col.key) ||
													pinnedRightColumns.includes(col.key)) &&
													"border-r-0",
												"font-semibold",
											)}
											style={getFooterStyle(
												col.key,
												false,
												index === orderedColumns.length - 1,
											)}
											data-column-key={col.key}
										>
											{index === 0 && "Total"}
											{totalsRow[col.key] || ""}
										</TableCell>
									))}
								</TableRow>
							</tfoot>
						)}
					</table>
				</div>

				{/* Infinite Scroll */}
				{infiniteScroll?.enabled && !pagination?.enabled && (
					<>
						<div ref={sentinelRef} className="h-1 w-full" />
						<div className="px-4 py-2 border-t flex-shrink-0">
							<span className="text-sm text-muted-foreground">
								{visibleRows >= filteredData.length
									? `All ${filteredData.length} results loaded`
									: `Showing ${Math.min(visibleRows, filteredData.length)} of ${filteredData.length} results`}
							</span>
						</div>
					</>
				)}

				{/* Pagination */}
				{pagination?.enabled && (
					<div className="flex items-center justify-between px-3 py-1.5 border-t flex-shrink-0 bg-white">
						{/* Page size selector */}
						<div className="flex items-center gap-2 text-sm text-muted-foreground">
							<span>Rows per page:</span>
							<Select
								value={String(pageSize)}
								onValueChange={(val) => {
									const newSize = Number(val);
									setPageSize(newSize);
									setCurrentPage(1);
									pagination?.onPageSizeChange?.(newSize);
								}}
							>
								<SelectTrigger className="h-8 w-20 text-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{pageSizeOptions.map((opt) => (
										<SelectItem key={opt} value={String(opt)}>
											{opt}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<span className="text-sm text-muted-foreground">
								{`Showing ${paginationTotal === 0 ? 0 : (currentPage - 1) * pageSize + 1} - ${Math.min(currentPage * pageSize, paginationTotal)} of ${paginationTotal} results`}
							</span>
						</div>

						{/* Page navigation — design system Pagination components */}
						<Pagination className="mx-0 w-auto">
							<PaginationContent>
								<PaginationItem>
									<PaginationPrevious
										onClick={
											currentPage > 1
												? () => {
														const newPage = currentPage - 1;
														setCurrentPage(newPage);
														pagination?.onPageChange?.(newPage);
													}
												: undefined
										}
										aria-disabled={currentPage === 1}
										className={
											currentPage === 1
												? "pointer-events-none opacity-50"
												: "cursor-pointer"
										}
									/>
								</PaginationItem>

								{Array.from({ length: totalPages }, (_, i) => i + 1)
									.filter((page) => {
										if (totalPages <= 7) return true;
										if (page === 1 || page === totalPages) return true;
										if (Math.abs(page - currentPage) <= 1) return true;
										return false;
									})
									.reduce<(number | "ellipsis-start" | "ellipsis-end")[]>(
										(acc, page, idx, arr) => {
											if (
												idx > 0 &&
												typeof arr[idx - 1] === "number" &&
												(page as number) - (arr[idx - 1] as number) > 1
											) {
												acc.push(
													page <= currentPage
														? "ellipsis-start"
														: "ellipsis-end",
												);
											}
											acc.push(page);
											return acc;
										},
										[],
									)
									.map((item) =>
										item === "ellipsis-start" || item === "ellipsis-end" ? (
											<PaginationItem key={String(item)}>
												<PaginationEllipsis />
											</PaginationItem>
										) : (
											<PaginationItem key={item}>
												<PaginationLink
													isActive={currentPage === item}
													onClick={() => {
														setCurrentPage(item as number);
														pagination?.onPageChange?.(item as number);
													}}
													className="cursor-pointer"
												>
													{item}
												</PaginationLink>
											</PaginationItem>
										),
									)}

								<PaginationItem>
									<PaginationNext
										onClick={
											currentPage < totalPages
												? () => {
														const newPage = currentPage + 1;
														setCurrentPage(newPage);
														pagination?.onPageChange?.(newPage);
													}
												: undefined
										}
										aria-disabled={currentPage === totalPages}
										className={
											currentPage === totalPages
												? "pointer-events-none opacity-50"
												: "cursor-pointer"
										}
									/>
								</PaginationItem>
							</PaginationContent>
						</Pagination>
					</div>
				)}
			</Card>

			{showAdvancedFilter && (
				<AdvancedFilterPanel
					isOpen={isAdvancedFilterOpen}
					onClose={() => setIsAdvancedFilterOpen(false)}
					columns={columns.map((col) => ({
						key: col.key,
						label: col.label,
						sortable: col.sortable,
						filterable: col.filterable,
						filterConfig: col.filterConfig,
						align: col.align,
						render: () => null,
					}))}
					filters={columnFilters}
					onApply={(newFilters) => {
						setColumnFilters(newFilters);
						if (onFilterChange) onFilterChange(newFilters);
						setIsAdvancedFilterOpen(false);
					}}
					onClearAll={() => {
						setColumnFilters({});
						if (onFilterChange) onFilterChange({});
					}}
				/>
			)}

			<ConfigureColumnsDialog
				open={isConfigureColumnsOpen}
				onOpenChange={setIsConfigureColumnsOpen}
				availableColumns={columns
					.filter(
						(col) =>
							!pinnedLeftColumns.includes(col.key) &&
							!pinnedRightColumns.includes(col.key),
					)
					.map((col) => ({
						key: col.key,
						label: col.label,
						description:
							`${col.sortable ? "Sortable" : ""} ${col.filterable ? "Filterable" : ""}`.trim() ||
							undefined,
					}))}
				visibleColumns={visibleColumnKeys.filter(
					(key) =>
						!pinnedLeftColumns.includes(key) &&
						!pinnedRightColumns.includes(key),
				)}
				onSave={(newVisibleKeys) => {
					const allPinned = [...pinnedLeftColumns, ...pinnedRightColumns];
					setVisibleColumnKeys([...allPinned, ...newVisibleKeys]);
					onColumnConfigSave?.([...allPinned, ...newVisibleKeys]);
				}}
			/>
		</>
	);
}
